import React from 'react';
import { ArrowLeft, Github, ExternalLink, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import BeeSolarInternship from '../assets/achivements/BeeSolarInternship.png';


const Bee_Solar_Website: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Bee Solar Power Product Website</h1>
            
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">React</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Tailwind CSS</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">JavaScript</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Responsive Design</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
                <img
                  src={BeeSolarInternship}
                  alt="Bee Solar Power Product Website"
                  className="w-full object-cover"
                />
              </div>
              <div>
                <img src="https://via.placeholder.com/1200x600?text=Product+Showcase" alt="Product Showcase" className="w-full object-cover" />
              </div>
              <div>
                <img src="https://via.placeholder.com/1200x600?text=Customer+Testimonials" alt="Customer Testimonials" className="w-full object-cover" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Developed a responsive, modern website showcasing Bee Solar's product portfolio, featuring 
                  interactive product displays, solar calculator, and customer testimonials with a focus on 
                  clean, renewable energy solutions. The website serves as the primary digital presence for 
                  Bee Solar, a leading provider of solar power solutions for residential and commercial applications.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Development Approach</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The website was built using a mobile-first approach to ensure optimal viewing experience across 
                  all devices. I implemented a clean, intuitive interface that emphasizes product imagery while 
                  providing detailed specifications and benefits for each solar solution. The design incorporates 
                  the company's branding elements throughout, with a color scheme inspired by natural sunlight and 
                  sustainable energy.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li>Interactive product showcase with 360-- views of solar panels and installations</li>
                  <li>Solar energy calculator to estimate potential savings and ROI</li>
                  <li>Location-based solar efficiency estimator</li>
                  <li>Animated infographics explaining solar technology benefits</li>
                  <li>Customer testimonial carousel with project imagery</li>
                  <li>Request quote form with integrated validation</li>
                  <li>Blog section for renewable energy news and tips</li>
                  <li>Dealer/installer locator map</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The website was built using React for the frontend, with Tailwind CSS providing the styling 
                  framework. This combination allowed for rapid development of responsive components while 
                  maintaining a consistent design language. Key interactive elements were implemented using 
                  custom JavaScript modules, including the solar calculator which pulls data from a solar 
                  irradiance API based on the user's location. The site's performance was optimized through 
                  lazy loading of images, code splitting, and efficient asset delivery through a CDN.
                </p>
              </div>
              
              {/* Live site mockup */}
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Website Design</h2>
                <div className="aspect-video w-full overflow-hidden rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-600/10 p-8">
                  <h3 className="text-xl font-medium mb-6 text-center">Site Features Preview</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <Globe className="h-5 w-5 text-primary" />
                      </div>
                      <p className="text-sm font-medium">Product Showcase</p>
                    </div>
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 3v18"/>
                          <path d="M3 12h18"/>
                          <path d="M12 3l-4 4"/>
                          <path d="M12 3l4 4"/>
                          <path d="M12 21l-4-4"/>
                          <path d="M12 21l4-4"/>
                        </svg>
                      </div>
                      <p className="text-sm font-medium">Solar Calculator</p>
                    </div>
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                      </div>
                      <p className="text-sm font-medium">Customer Support</p>
                    </div>
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 8h4v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8h4"/>
                          <path d="M12 10v6"/>
                          <path d="M9 13h6"/>
                          <path d="M16 3l-4 4-4-4"/>
                        </svg>
                      </div>
                      <p className="text-sm font-medium">Quick Quote</p>
                    </div>
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 10c0 3.976-7 11-7 11s-7-7.024-7-11 3.134-7 7-7 7 3.024 7 7z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                      </div>
                      <p className="text-sm font-medium">Dealer Locator</p>
                    </div>
                    <div className="rounded-md bg-white/10 p-4 text-center">
                      <div className="h-10 w-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary/20">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                          <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                          <path d="M9 12h6"/>
                          <path d="M9 16h6"/>
                        </svg>
                      </div>
                      <p className="text-sm font-medium">Product Catalog</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/bee-solar-website" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://beesolarspower.web.app" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Globe className="mr-2 h-5 w-5" />
                      <span>Live Website</span>
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">R</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">React</p>
                      <p className="text-sm text-muted-foreground">Frontend framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">T</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Tailwind CSS</p>
                      <p className="text-sm text-muted-foreground">Styling framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">J</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">JavaScript</p>
                      <p className="text-sm text-muted-foreground">Programming language</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">R</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Responsive Design</p>
                      <p className="text-sm text-muted-foreground">Mobile-first approach</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Outcome</h3>
                <p className="text-muted-foreground mb-4">
                  The Bee Solar website delivers an engaging user experience that effectively showcases 
                  the company's solar products while educating visitors about renewable energy benefits.
                </p>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Design Quality</span>
                  <span className="text-primary font-medium">Excellent</span>
                </div>
                <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full w-[90%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                </div>
                <div className="flex justify-between items-center mb-2 mt-4">
                  <span className="text-muted-foreground">User Experience</span>
                  <span className="text-primary font-medium">High</span>
                </div>
                <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full w-[85%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Avinyaa_2025" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Avinyaa 2025 Website</h3>
              <p className="text-muted-foreground line-clamp-2">A website for a college symposium with modern web technologies.</p>
            </Link>
            
            <Link to="/Inventory_Management_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Inventory Management System</h3>
              <p className="text-muted-foreground line-clamp-2">A system designed to streamline stock tracking and optimize warehouse operations.</p>
            </Link>
            
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A self-hosted cloud storage solution using TrueNAS on a repurposed machine.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Bee_Solar_Website; 
